import React from 'react'
import { Box, Typography } from '@mui/material'
import HomeIcon from '@mui/icons-material/Home'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'

const colors = {
  white: '#FFFFFF',
  primary: '#324755',
  accent: '#FF6F61',
  headerGradient: 'linear-gradient(90deg, #2C3E50, #34495E)',
  headerBorder: 'rgba(255,255,255,0.2)',
  label: '#87BCBF',
}

const parseNumber = (value) => {
  if (typeof value === 'string') {
    const cleanedValue = value.replace(/[^0-9.-]+/g, '')
    return Number(cleanedValue)
  }
  return Number(value)
}

export const StatHeader = ({ title, IconComponent }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      px: 1.5,
      py: 0.5,
      background: colors.headerGradient,
      borderBottom: `2px solid ${colors.headerBorder}`,
      flexShrink: 0,
    }}
  >
    {IconComponent && (
      <Box
        sx={{
          width: 45,
          height: 45,
          borderRadius: '50%',
          background: colors.white,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          mr: 2,
        }}
      >
        <IconComponent sx={{ fontSize: 24, color: colors.primary }} />
      </Box>
    )}
    <Typography
      sx={{
        fontWeight: 700,
        color: colors.white,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        fontSize: 16,
      }}
    >
      {title}
    </Typography>
  </Box>
)

const formatNumber = (num, prefix = '') => {
  const n = Number(num)
  if (n >= 1e9) return prefix + (n / 1e9).toFixed(2) + 'B'
  if (n >= 1e6) return prefix + (n / 1e6).toFixed(2) + 'M'
  if (n >= 1e3) return prefix + (n / 1e3).toFixed(2) + 'K'
  return prefix + n.toLocaleString('en-US', { maximumFractionDigits: 0 })
}

const StatWidget = ({ title, value, label }) => {
  const IconComponent =
    title === 'Structure Impacts'
      ? HomeIcon
      : title === 'Damage Estimate'
      ? AttachMoneyIcon
      : null

  const numericValue = parseNumber(value) || 0
  const formattedValue =
    title === 'Damage Estimate'
      ? formatNumber(numericValue, '$')
      : formatNumber(numericValue)

  return (
    <Box
      sx={{
        borderRadius: '16px',
        overflow: 'hidden',
        background: colors.white,
        boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
        transition: 'transform 0.3s, box-shadow 0.3s',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%', // Ensures the widget spans the full width of its container
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: '0 12px 24px rgba(0,0,0,0.2)',
        },
      }}
    >
      <StatHeader title={title} IconComponent={IconComponent} />
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          p: 3,
          textAlign: 'center',
        }}
      >
        <Typography
          variant="h2"
          sx={{ fontWeight: 'bold', color: colors.primary, mb: 1 }}
        >
          {formattedValue}
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{
            color: colors.label,
            textTransform: 'uppercase',
            fontWeight: 600,
          }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  )
}

export default StatWidget
